import { useState } from "react";
import { useParams } from "react-router-dom";
import { useOrder } from "hooks/use-order";
import { useOrderActions } from "hooks/use-order-actions";
import { usePaths } from "BEDROCK/paths";
import styles from "./admin-order-detail-page.module.css";
import { StatusDropdown } from "BEDROCK/components/ui-status-dropdown/ui-status-dropdown";
import { ORDER_STATUSES, OrderStatus } from "types/order-status";
import { CollapsibleJsonViewer } from "BEDROCK/components/collapsible-json-viewer/collapsible-json-viewer";

const CopyableId = ({ label, value }: { label: string; value: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={styles.infoRow}>
      <span className={styles.label}>{label}:</span>
      <span
        className={`${styles.value} ${styles.copyableId}`}
        onClick={handleCopy}
        title="Click to copy"
      >
        {value}
        <span className={styles.copyIndicator}>{copied ? "✓" : "⧉"}</span>
      </span>
    </div>
  );
};

const AddressSection = ({ address }: { address: Record<string, unknown> }) => {
  const [copied, setCopied] = useState(false);

  // Format address for clipboard - only include non-null values
  const formatAddressForCopy = () => {
    return Object.entries(address)
      .filter(([, value]) => value != null && value !== "")
      .map(([, value]) => String(value))
      .join("\n");
  };

  const handleCopyAddress = async () => {
    await navigator.clipboard.writeText(formatAddressForCopy());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={styles.section}>
      <div className={styles.addressHeader}>
        <h2>Delivery Address</h2>
        <button
          onClick={handleCopyAddress}
          className={styles.copyAddressButton}
          title="Copy address to clipboard"
        >
          {copied ? "✓ Copied" : "Copy Address"}
        </button>
      </div>
      <div className={styles.address}>
        {Object.entries(address).map(([key, value]) => (
          <div key={key} className={styles.addressRow}>
            <span className={styles.addressLabel}>{key}:</span>
            <span className={styles.addressValue}>
              {value != null && value !== "" ? String(value) : ""}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

interface AdminOrderDetailPageProps {}

export const AdminOrderDetailPage = (_props: AdminOrderDetailPageProps) => {
  const { orderId } = useParams<{ orderId: string }>();
  const {
    order,
    isLoading: isLoadingOrder,
    isFetching,
    isError,
  } = useOrder(orderId);
  const {
    approveOrder,
    rejectOrder,
    updateOrderStatus,
    isLoading: isLoadingAction,
  } = useOrderActions();
  const { navigateTo, orders } = usePaths();

  if (isLoadingOrder) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading order details...</div>
      </div>
    );
  }

  if (isError || !order) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          Order not found. Please go back to the orders list.
        </div>
        <button
          onClick={() => navigateTo(orders)}
          className={styles.backButton}
        >
          Back to Orders
        </button>
      </div>
    );
  }

  const handleApprove = async () => {
    try {
      await approveOrder(order.id);
      navigateTo(orders);
    } catch (error) {
      console.error("Error approving order:", error);
      alert("Failed to approve order. Please try again.");
    }
  };

  const handleReject = async () => {
    try {
      await rejectOrder(order.id);
      navigateTo(orders);
    } catch (error) {
      console.error("Error rejecting order:", error);
      alert("Failed to reject order. Please try again.");
    }
  };

  const handleStatusChange = async (newStatus: OrderStatus) => {
    try {
      await updateOrderStatus(order.id, newStatus);
      // Don't navigate away, just let the query refetch
    } catch (error) {
      console.error("Error updating order status:", error);
      alert("Failed to update order status. Please try again.");
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button
          onClick={() => navigateTo(orders)}
          className={styles.backButton}
        >
          ← Back to Orders
        </button>
        <h1>Order Details</h1>
      </div>

      <div className={styles.content}>
        <div className={styles.section}>
          <h2>Order Information</h2>
          <div className={styles.infoGrid}>
            <CopyableId label="Order ID" value={order.id} />
            <CopyableId label="Order Number" value={order.id_number || "N/A"} />
            <div className={styles.infoRow}>
              <span className={styles.label}>Status:</span>
              <span className={`${styles.value} ${styles.status}`}>
                {order.status}
              </span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.label}>Created:</span>
              <span className={styles.value}>
                {new Date(order.created_at).toLocaleString()}
              </span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.label}>Price:</span>
              <span className={styles.value}>
                £{(order.price / 100).toFixed(2)}
              </span>
            </div>
            <CopyableId
              label="Stripe Session"
              value={order.stripe_session_id || "N/A"}
            />
          </div>
        </div>

        <div className={styles.section}>
          <h2>User Information</h2>
          <div className={styles.infoGrid}>
            <CopyableId label="User ID" value={order.user_id} />
            <CopyableId label="Email" value={order.user_email} />
            <div className={styles.infoRow}>
              <span className={styles.label}>Full Name:</span>
              <span className={styles.value}>
                {order.user_full_name || "N/A"}
              </span>
            </div>
          </div>
        </div>

        {order.delivery_address && (
          <AddressSection address={order.delivery_address} />
        )}

        <div className={styles.section}>
          <h2>Projects ({order.project_ids.length})</h2>
          {order.project_ids.map((projectId) => {
            const projectDetails = order.project_details?.[projectId];

            const handleDownload = async () => {
              if (!projectDetails?.imageUrl) return;

              try {
                const response = await fetch(projectDetails.imageUrl);
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = `project-${projectId.substring(
                  0,
                  8
                )}-render.png`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
              } catch (error) {
                console.error("Error downloading image:", error);
                alert("Failed to download image. Please try again.");
              }
            };

            // Use thumbnailUrl if available, fallback to imageUrl
            const displayUrl = projectDetails?.thumbnailUrl || projectDetails?.imageUrl;

            return (
              <div key={projectId} className={styles.projectSection}>
                <CopyableId label="Project" value={projectId} />

                <div className={styles.projectContent}>
                  <div className={styles.renderSection}>
                    {displayUrl ? (
                      <img
                        src={displayUrl}
                        alt={`Preview for ${projectId}`}
                        className={styles.renderImage}
                      />
                    ) : isFetching ? (
                      <div className={styles.imagePlaceholder}>
                        Loading image...
                      </div>
                    ) : (
                      <div className={styles.imagePlaceholder}>
                        Image not available
                      </div>
                    )}
                    {projectDetails?.imageUrl && (
                      <button
                        onClick={handleDownload}
                        className={styles.downloadButton}
                      >
                        Download Render
                      </button>
                    )}
                  </div>

                  <div className={styles.projectInfo}>
                    {projectDetails?.physicalProduct && (
                      <div className={styles.physicalProduct}>
                        <h4>Physical Product</h4>
                        <div className={styles.productDetails}>
                          <div className={styles.infoRow}>
                            <span className={styles.label}>SKU:</span>
                            <span className={styles.value}>
                              {projectDetails.physicalProduct.sku}
                            </span>
                          </div>
                          <div className={styles.infoRow}>
                            <span className={styles.label}>Size:</span>
                            <span className={styles.value}>
                              {projectDetails.physicalProduct.size_description}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {projectDetails?.json && (
                      <CollapsibleJsonViewer
                        data={projectDetails.json}
                        label="View Project JSON"
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className={styles.section}>
          <h2>Order Status</h2>
          <div className={styles.statusControls}>
            {order.status === "created" && (
              <div className={styles.quickActions}>
                <button
                  onClick={handleApprove}
                  disabled={isLoadingAction}
                  className={styles.approveButton}
                >
                  {isLoadingAction ? "Processing..." : "Approve Order"}
                </button>
                <button
                  onClick={handleReject}
                  disabled={isLoadingAction}
                  className={styles.rejectButton}
                >
                  {isLoadingAction ? "Processing..." : "Reject Order"}
                </button>
              </div>
            )}
            <div className={styles.statusDropdownWrapper}>
              <StatusDropdown
                currentStatus={order.status as OrderStatus}
                availableStatuses={ORDER_STATUSES}
                onStatusChange={handleStatusChange}
                disabled={isLoadingAction}
                label="Update status to:"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
